import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { GridIcon } from '@tanium/react-data-grid';
import { useTranslation } from 'react-i18next';
import { RibbonColumn, SortChangeEvent, SortItem, StackedRibbonListStatic } from '@tanium/react-stacked-ribbon-list';
import { PageSection } from '@tanium/react-page-section';
import { Stack } from '@tanium/react-stack';
import { Box } from '@tanium/react-box';
import { Shimmer } from '@tanium/react-shimmer';
import { TextWithIcon } from '@tanium/react-text-with-icon';
import { Button, TooltippedButton } from '@tanium/react-button';
import { PersonIcon, EditIcon, TrashIcon } from '@tanium/react-graphics';
import { useHasLinkedCloudInstances } from '../../hooks/useHasLinkedCloudInstances';
import { useIdentityProviderConfigs } from '../../hooks/useIdentityProviderConfigs';
import { AsyncStatus } from '../../hooks/useAsync';
import { IdpSetupModal } from '../../components/IdpModals/IdpSetupModal';
import { IdpScope, IdpScopeValue } from '../../components/IdpModals/IdpScopeCard';
import { DeleteIdpModal } from '../../components/IdpModals/IdpDeleteModal';
import { useModalState } from '../../hooks/useModalState';
import { AsyncSuspense } from '../../components/AsyncSuspense';
import { sortData } from '../../utilities/commonFuncs';
import { ConfigureIdpRequest, IdpResponse } from '../../models/IdpModels';
import { useHasPermissions } from '../../hooks/useUserPermissions';
import { Permission } from '../../models/Permissions';
import { SmallSubtleText } from '../../TextStyles';

interface IdentityProvidersSectionProps {
  showInstanceFriendlyNames?: boolean;
}

export const IdentityProvidersSection: React.VFC<IdentityProvidersSectionProps> = ({
  showInstanceFriendlyNames = false,
}) => {
  const { t } = useTranslation();
  const [addScope, setAddScope] = useState<IdpScopeValue>(IdpScope.Account);
  const hasCloudInstances = useHasLinkedCloudInstances();
  const [dataSort, setDataSort] = useState<SortItem[]>([]);
  const { loadIdps, createIdp, updateIdp } = useIdentityProviderConfigs();
  const isLoading = loadIdps.status === AsyncStatus.Pending;
  const idpConfigurations = useMemo(() => sortData(dataSort, loadIdps.value ?? []), [loadIdps.value, dataSort]);
  const dataGetter = (i: number): IdpResponse => idpConfigurations[i];

  const addOrUpdateModal = useModalState<ConfigureIdpRequest>();
  const deleteModal = useModalState<IdpResponse>();

  const location = useLocation();
  const history = useHistory();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('openAddIdp') === 'true') {
      setAddScope(IdpScope.Account);
      addOrUpdateModal.openModal();
      history.replace({ search: '' });
    }
  }, [location.search]);

  const canCreateOrUpdateIdp = useHasPermissions([Permission.CreateIdp, Permission.UpdateIdp]);
  const canDeleteIdp = useHasPermissions([Permission.DeleteIdp]);

  const tooltipAddIDP = useMemo((): string => {
    if (canCreateOrUpdateIdp) {
      return t('Configure identity provider settings for either Tanium Account or Tanium Console');
    }
    return t('You have insufficient permissions to add an IDP.');
  }, [canCreateOrUpdateIdp, t]);

  const onSortChange = useCallback(({ sort }: SortChangeEvent) => {
    setDataSort(sort);
  }, []);

  const columns: RibbonColumn<IdpResponse>[] = [
    {
      label: t('Name'),
      dataKey: 'displayName',
    },
    {
      label: t('Sign In Email Domains'),
      dataKey: 'domainNames',
      renderer: ({ itemData }) => itemData.domainNames?.join(', '),
    },
    {
      label: t('Actions'),
      dataKey: '',
      alignment: 'center',
      width: 80,
      renderer: (field) => (
        <span>
          <Stack itemSpacing={2}>
            {canCreateOrUpdateIdp && (
              <GridIcon
                icon={EditIcon}
                label={t('Edit')}
                onClick={() => {
                  addOrUpdateModal.openModal(field.itemData);
                }}
              />
            )}
            {canDeleteIdp && (
              <GridIcon
                icon={TrashIcon}
                label={t('Delete')}
                onClick={() => {
                  deleteModal.openModal(field.itemData);
                }}
              />
            )}
          </Stack>
        </span>
      ),
      isSortDisabled: true,
    },
  ];

  return (
    <PageSection
      title={<TextWithIcon leadingIcon={PersonIcon}>{t('Identity Provider Settings')}</TextWithIcon>}
      additionalTitleContent={
        <Stack width="100%" direction="row" justifyContent="right" alignItems="center">
          <TooltippedButton
            size="sm"
            disabled={!canCreateOrUpdateIdp}
            tooltipContent={tooltipAddIDP}
            onClick={() => {
              setAddScope(IdpScope.Account);
              addOrUpdateModal.openModal();
            }}
          >
            {t('Add IDP')}
          </TooltippedButton>
        </Stack>
      }
    >
      <Stack direction="row" itemSpacing={1} alignItems="center" flexWrap="wrap">
        <SmallSubtleText>
          {t(
            'Controls sign-in to Tanium Account. Tanium Console sign-in is configured separately for each Tanium Cloud instance.'
          )}
        </SmallSubtleText>
        {canCreateOrUpdateIdp && hasCloudInstances && (
          <Button
            variant="link"
            size="sm"
            onClick={() => {
              setAddScope(IdpScope.Console);
              addOrUpdateModal.openModal();
            }}
          >
            {t('Configure Tanium Console sign-in')}
          </Button>
        )}
      </Stack>
      <AsyncSuspense
        state={loadIdps}
        loading={
          <Box mt={2} height={1} width="100%">
            <Shimmer />
          </Box>
        }
        failure={t('Failed to load Identity Provider Settings')}
      >
        <StackedRibbonListStatic<string, IdpResponse>
          columns={columns}
          dataGetter={dataGetter}
          isItemLoaded={() => !isLoading}
          itemCount={idpConfigurations.length}
          totalItemCount={idpConfigurations.length}
          itemIdGetter={(i) => dataGetter(i)?.displayName}
          isLoading={isLoading}
          onSortChange={onSortChange}
          sort={dataSort}
        ></StackedRibbonListStatic>
      </AsyncSuspense>
      <IdpSetupModal
        initialConfig={addOrUpdateModal.data}
        initialScope={addScope}
        showInstanceFriendlyNames={showInstanceFriendlyNames}
        isOpen={addOrUpdateModal.isModalOpen}
        onRequestClose={() => {
          createIdp.reset();
          updateIdp.reset();
          addOrUpdateModal.closeModal();
        }}
      />
      <DeleteIdpModal idp={deleteModal.data} isOpen={deleteModal.isModalOpen} onRequestClose={deleteModal.closeModal} />
    </PageSection>
  );
};
